/**
 * This view shows MoMa Panorama with use of TTPanorama component.
 * Uses Splitpanes to divide window into 2 parts:
 * current panorama and its vicinity map.
 * TTMap in the second splitpane is in read-only mode,
 * locking UI from most user interactions, fixing the vicinity map
 * on selected point.
 */
<template>
    <div id="Panorama" ref="container">
        <splitpanes watch-slots class="default-theme" horizontal style="height: 100vh"
            @resize="fitPanesWithFix()"
            @resized="resizedPanes($event)">
            <pane
                class="map panorama"
                :min-size="minPaneSize"
                :max-size="100 - minPaneSize"
                :size="splitpanesSize">
                <router-link
                    class="link-return" to="/">
                    <LocationInfo
                        :title="reverseGeocodeDetails.title"
                        :detailAddress="reverseGeocodeDetails.detailAddress"
                        />
                </router-link>
                <div
                    ref="loader" class="panorama-loader"
                    :class="{ 'is-hidden': !isLoading }"></div>
                <TTPanorama
                    ref="panorama"
                    :momaURLBuilder="momaURL"
                    :panorama="panorama"
                    :initialOrientation="orientation"
                    :gamma="gamma"
                    @selected="selectCoordinates"
                    @loadBegin="setLoadingStatus(true)"
                    @loadEnd="setLoadingStatus(false)"
                    @orientationChanged="setOrientation"></TTPanorama>
                <div class="column column-options tt-panorama">
                    <div class="navigation-button">
                        <button class="button is-navigate-small"
                            @click="zoomIn">
                            <img
                                src="../assets/icon-zoom-in-white.svg"
                                height="20px" width="20px" />
                        </button>
                        <button class="button is-navigate-small"
                            @click="zoomOut">
                            <img
                                src="../assets/icon-zoom-out-white.svg"
                                height="20px" width="20px" />
                        </button>
                        <button class="button details-btn is-navigate-small"
                            :class="{
                                    'details-btn-active': informationVisible,
                                    'details-btn-inactive': !informationVisible,
                                }"
                            @click="toggleInformationVisible()">
                            <img
                                :class="{ 'inverted': !informationVisible }"
                                src="../assets/icon-informac-black.svg"/>
                        </button>

                        <LocationDetailInfo
                            v-if="informationVisible"
                            :dateCreated="selectedDate.date"
                            :timeCreated="selectedTime.time"
                            :coordinates="selectedCoordinates"
                            :sessionName="selectedSessionName"
                            :sequentialNumber="selectedSequenceNumber"/>

                        <button class="button details-btn is-navigate-small"
                            @click="gammaPanelToggler"
                            :class="{ 'details-btn-active': gammaPanel }">
                            <img
                                src="../assets/gamma.svg"
                                :class="{ 'inverted': gammaPanel }"/>
                        </button>

                        <div class="gammaPanel" v-if="gammaPanel">
                            <input
                                class="slider is-fullwidth is-small is-circle"
                                min="-1" max="1" :value="gamma" step="0.1" type="range"
                                @input="gammaValue">
                        </div>
                    </div>
                </div>
            </pane>
            <pane
                :min-size="minPaneSize"
                :max-size="100 - minPaneSize"
                :size="100 - splitpanesSize">
                    <TTMap
                        ref="map"
                        :apiKey="apiKey"
                        :momaURLBuilder="momaURL"
                        :readOnly="false"
                        :arrow="marker"
                        :orientation="currentOrientation"
                        :center="panoramaCenter"
                        :zoomLevel="16"
                        :initialLayer="layer"
                        :initialStartDate="startDate"
                        :initialEndDate="endDate"
                        @selectedCoordinates="selectCoordinates"/>
            </pane>
        </splitpanes>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

import LocationInfo from '../components/LocationInfo.vue';
import LocationDetailInfo from '../components/LocationDetailInfo.vue';
import TTMap from '../components/TTMap.vue';
import TTPanorama from '../components/TTPanorama.vue';

export default {
    name: 'Panorama',

    data() {
        return {
            /**
             * Flag to control of visibility of the gamma slider panel.
             * Default is false for hide gamma slider panel.
             */
            gammaPanel: false,
        };
    },

    components: {
        LocationInfo,
        LocationDetailInfo,
        TTMap,
        TTPanorama,
        Splitpanes,
        Pane,
    },

    computed: {
        ...mapGetters(['momaURL', 'panoramaCenter']),
        ...mapGetters('panorama', [
            'panoramaCenter',
            'reverseGeocodeDetails',
            'minPaneSize',
            'selectedSessionName',
            'selectedSequenceNumber',
            'selectedCoordinates',
            'selectedDate',
            'selectedTime',
        ]),
        ...mapState(['apiKey']),
        ...mapState('panorama', [
            'splitpanesSize',
            'panorama',
            'isLoading',
            'orientation',
            'informationVisible',
            'gamma',
        ]),
        ...mapState('map', ['layer']),
        ...mapGetters('map', ['startDate', 'endDate']),

        /**
         * Marker shown on TTMap component, representing the center
         * point of current panorama.
         * @type {Array}
         */
        marker() {
            const { lat, lng } = this.panoramaCenter;
            return { position: { lat, lng } };
        },

        /**
         * Return the current orientation of the user.
         */
        currentOrientation() {
            const { orientation } = this;
            if (!orientation) {
                return 0.0;
            }

            const { yaw = 0.0 } = orientation;
            return yaw;
        },
    },

    mounted() {
        window.addEventListener('resize', this.setWindowHeight);
        this.$nextTick(() => {
            this.setWindowHeight();
            this.fitPanesWithFix();
        });
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setWindowHeight);
    },

    methods: {
        ...mapActions('panorama', [
            'selectCoordinates',
            'setSplitpanesSize',
            'setContainerHeight',
            'setLoadingStatus',
            'setOrientation',
            'toggleInformationVisible',
            'setGamma',
        ]),

        /**
         * Used to fit (update the content) both panes in Splitpanes
         * during panes resize.
         */
        fitPanes() {
            this.$refs.panorama.fit();
            this.$refs.map.fit();
        },

        /**
         * This method fixes the white space on canvases when dragging
         * the panes.
         */
        fitPanesWithFix() {
            this.fitPanes();

            this.$nextTick(() => {
                this.fitPanes();
            });
        },

        setWindowHeight() {
            this.setContainerHeight(this.$refs.container.clientHeight);
        },

        /**
         * Used to set store state pane width variable after the panes have
         * been resized.
         * @param {Object} event
         * @param {number} event.width First pane width (vertical).
         */
        resizedPanes([{ size }]) {
            this.setSplitpanesSize(Math.round(size));
        },

        zoomIn() {
            this.$refs.panorama.zoomIn();
        },

        zoomOut() {
            this.$refs.panorama.zoomOut();
        },

        /**
         * Used to get slider updated value and insert it
         * to setGamma method to set gamma state.
         */
        gammaValue(event) {
            const gamma = event.target.value;
            this.setGamma(gamma);
        },

        /**
         * Toggle of visibility gamma slider panel.
         */
        gammaPanelToggler() {
            this.gammaPanel = !this.gammaPanel;
        },
    },

    watch: {
        splitpanesSize() {
            this.fitPanesWithFix();
        },
    },
};
</script>
